import { createContext, useContext, useEffect, useState } from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import { ROUTE_LABELS } from '@ave/shared';
import { api, ApiError, type Me } from './lib/api';
import { Gate } from './components/Gate';
import { Icon } from './components/icons';
import { Badge, Spinner } from './components/ui';
import { EngagementsPage } from './pages/Engagements';
import { WorkspacePage } from './pages/Workspace';
import { AdminPage } from './pages/Admin';

const SessionContext = createContext<Me | null>(null);
export const useSession = (): Me => {
  const me = useContext(SessionContext);
  if (!me) throw new Error('useSession used outside a session');
  return me;
};

const PORTAL_URL = 'https://www.acumon-intelligence.com';

export default function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [gate, setGate] = useState<{ launchUrl: string; message?: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .me()
      .then(setMe)
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          setGate({
            launchUrl: err.launchUrl ?? 'https://admin.acumon-intelligence.com/launch/valuations',
            message: err.message,
          });
        } else {
          setGate({
            launchUrl: 'https://admin.acumon-intelligence.com/launch/valuations',
            message:
              err instanceof Error
                ? `${err.message} — is the API running on port 8890?`
                : 'Could not reach the Valuations API.',
          });
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Spinner label="Checking your session" />
      </div>
    );
  }

  if (!me) {
    return (
      <Gate
        launchUrl={gate?.launchUrl ?? 'https://admin.acumon-intelligence.com/launch/valuations'}
        message={gate?.message}
        devLaunchAvailable={import.meta.env.DEV}
      />
    );
  }

  return (
    <SessionContext.Provider value={me}>
      <div className="min-h-screen bg-slate-50">
        <TopBar me={me} />
        <main className="mx-auto max-w-[1400px] px-6 pb-20 pt-8">
          <Routes>
            <Route path="/" element={<EngagementsPage />} />
            <Route path="/engagements/:id" element={<WorkspacePage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="*" element={<EngagementsPage />} />
          </Routes>
        </main>
      </div>
    </SessionContext.Provider>
  );
}

function TopBar({ me }: { me: Me }) {
  const location = useLocation();
  const onWorkspace = location.pathname.startsWith('/engagements/');

  return (
    <>
      <div className="h-1 w-full bg-gradient-to-r from-teal-500 via-teal-600 to-emerald-600" />
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/85 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center gap-4 px-6">
          <a
            href={PORTAL_URL}
            className="group flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-slate-600"
            title="Back to Acumon Intelligence"
          >
            <Icon name="chevronRight" className="h-3.5 w-3.5 rotate-180" strokeWidth={2} />
            <span className="hidden sm:inline">Acumon Intelligence</span>
          </a>

          <span className="hidden h-5 w-px bg-slate-200 sm:block" />

          <Link to="/" className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-50">
              <Icon name="scale" className="h-[18px] w-[18px] text-teal-600" strokeWidth={1.7} />
            </span>
            <span className="text-[15px] font-semibold tracking-tight text-slate-900">
              Valuations
            </span>
          </Link>

          {onWorkspace ? (
            <Link
              to="/"
              className="ml-2 hidden items-center gap-1 text-sm text-slate-500 hover:text-slate-800 md:inline-flex"
            >
              <Icon name="list" className="h-4 w-4" />
              All engagements
            </Link>
          ) : null}

          <div className="ml-auto flex items-center gap-3">
            {/* Shown only to the role that can use it. Corridors and
                statutory amounts are platform-wide rows with no tenant, so
                editing them changes every firm on the system. */}
            {me.user.role === 'acumon_admin' ? (
              <Link
                to="/admin"
                className="text-sm text-slate-500 transition-colors hover:text-slate-800"
              >
                Reference data
              </Link>
            ) : null}
            {me.user.licenceReadOnly ? (
              <Badge tone="amber" title="This licence grants read-only access">
                <Icon name="lock" className="h-3 w-3" strokeWidth={2} />
                Read only
              </Badge>
            ) : null}
            <div className="hidden text-right sm:block">
              <p className="text-sm font-medium leading-tight text-slate-800">{me.user.name}</p>
              <p className="text-xs leading-tight text-slate-400">
                {me.tenant.name} · {ROUTE_LABELS[me.tenant.route].split('—')[1]?.trim()}
              </p>
            </div>
            <span
              className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-800 text-xs font-semibold text-white"
              title={`${me.user.email} · ${me.user.role}`}
            >
              {initials(me.user.name)}
            </span>
          </div>
        </div>
      </header>
    </>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}
